// src/AdminPanel.js
import React, { useState, useEffect } from "react";
import {
  Button,
  Form,
  Col,
  Row,
  Container,
  Card,
  Badge,
} from "react-bootstrap";
import {
  useWeb3ModalProvider,
  useWeb3ModalAccount,
} from "@web3modal/ethers/react";
import DateTimePicker from "react-datetime-picker";
import { useTranslation } from "react-i18next";
import {
  erc20Abi,
  isTestEnv,
  network,
  presaleAbi,
  presaleAddress,
  token,
  tokenAddress,
  usdtAddresss,
} from "./utils/contants";
import { toast } from "react-toastify";
import { Contract, ethers } from "ethers";
import { Decimal } from "decimal.js";
import "./AdminPanel.css";
import { getTimeZoneDifSeconds } from "./utils/utils.ts";
import { getSettings, setReferralCodeRequired } from "./store/firestore.ts";

const AdminPanel = () => {
  const { t, i18n } = useTranslation();
  const { address } = useWeb3ModalAccount();
  const { walletProvider } = useWeb3ModalProvider();

  const [presaleContract, setPresaleContract] = useState(null);
  const [tokenContract, setTokenContract] = useState(null);
  const [newPrice, setNewPrice] = useState("0");
  const [contractOwner, setContractOwner] = useState("");

  const [nativeBalance, setNativeBalance] = useState("0");
  const [usdtBalance, setUSDTBalance] = useState("0");
  const [tokenBalance, setTokenBalance] = useState("0");
  const [soldTokenAmount, setSoldTokenAmount] = useState("0");

  const [depositAmountToken, setDepositAmountToken] = useState("0");
  const [withdrawAmountToken, setWithDrawAmountToken] = useState("0");
  const [withdrawAmountUsdt, setWithdrawAmountUsdt] = useState("0");
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [price, setPrice] = useState(0);
  const [ownerAddress, setOwnerAddress] = useState("");
  const [salePaused, setSalePaused] = useState(false);

  const [startDateUpdate, setStartDateUpdate] = useState(new Date());
  const [endDateUpdate, setEndDateUpdate] = useState(new Date());
  const [settings, setSettings] = useState({
    referralCodeRequired: false,
  });

  const init = async () => {
    try {
      console.log("init called");
      if (!walletProvider) return;
      console.log("init worked");
      const provider = new ethers.BrowserProvider(walletProvider);
      const signer = await provider.getSigner();
      const _contract = new Contract(presaleAddress, presaleAbi, signer);
      setPresaleContract(_contract);
      const _tokenContract = new Contract(tokenAddress, erc20Abi, signer);
      setTokenContract(_tokenContract);
    } catch (error) {
      console.log(error);
      toast.error(t("info.error"));
    }
  };

  // Utility functions for splitting large numbers

  const getFirstPart = (num, decimals = 18) => {
    if (num.length <= decimals) return "0";
    return num.toString().substr(0, num.length - decimals);
  };
  const getLastPart = (num, decimals = 18) => {
    if (num.length <= decimals) {
      num = num.padStart(decimals, "0");
      return num.substr(0, 6).padEnd(6, "0");
    }
    return num
      .toString()
      .substr(num.length - decimals, decimals)
      .substr(0, 6);
  };
  const getFirstPartUSDT = (num) => {
    if (num.length <= 6) return "0";
    return num.toString().substr(0, num.length - 6);
  };
  const getLastPartUSDT = (num) => {
    if (num.length <= 6) {
      num = num.padStart(6, "0");
      return num.substr(0, 6).padEnd(6, "0");
    }
    return num.toString().substr(num.length - 6, 6);
  };

  const fetchBalances = async () => {
    try {
      if (!presaleContract) return;
      let _nativeBal = await presaleContract.getNativeBalance();
      let _lockedNative = await presaleContract.unWithdrawnAffProfitNative();
      setNativeBalance(
        new Decimal(_nativeBal.toString()).sub(_lockedNative).toFixed(0)
      );
      let _usdtBal = await presaleContract.getUsdtBalance();
      let _lockedAmount = await presaleContract.unWithdrawnAffProfitUSDT();
      setUSDTBalance(
        new Decimal(_usdtBal.toString())
          .sub(_lockedAmount.toString())
          .toFixed(0)
      );
      let _tokenBal = await presaleContract.getTokenBalance();
      setTokenBalance(new Decimal(_tokenBal.toString()).toFixed(0));
      let _soldAmount = await presaleContract.totalTokensSold();
      setSoldTokenAmount(
        new Decimal(_soldAmount.toString()).sub(95098).toString()
      );
    } catch (error) {
      console.log("Error fetching balances", error);
    }
  };

  const fetchDetails = async () => {
    try {
      if (!presaleContract) return;
      let _startTime = await presaleContract.startTime();
      let _endTime = await presaleContract.endTime();
      // Update startDate from Unix timestamp (in milliseconds)
      setStartDate(new Date(Number(_startTime) * 1000));
      setEndDate(new Date(Number(_endTime) * 1000));
      setEndDateUpdate(new Date(Number(_endTime) * 1000));
      setStartDateUpdate(new Date(Number(_startTime) * 1000));
      let _isPaused = await presaleContract.paused();
      setSalePaused(_isPaused);
      let _tokenPrice = await presaleContract.price();
      setPrice(_tokenPrice.toString());
      const ownerAddress = await presaleContract.owner();
      setContractOwner(ownerAddress);
      console.log(contractOwner);
    } catch (error) {
      console.log(error);
    }
  };

  // Update start date now accepts both date and time.
  const updateStartDate = async () => {
    try {
      // Convert startDate to Unix time (seconds)
      const unixTime = (
        Math.floor(startDateUpdate.getTime() / 1000) - getTimeZoneDifSeconds()
      ).toString();
      await (await presaleContract.changeSaleStartTime(unixTime)).wait();
      await fetchDetails();
      toast.success("Start date is updated");
    } catch (error) {
      console.log(error);
      toast.error(error.toString().substring(0, 100) + "...");
    }
  };

  // Update start date now accepts both date and time.
  const updateEndDate = async () => {
    try {
      // Convert startDate to Unix time (seconds)
      const unixTime = (
        Math.floor(endDateUpdate.getTime() / 1000) - getTimeZoneDifSeconds()
      ).toString();
      await (await presaleContract.changeSaleEndTime(unixTime)).wait();
      await fetchDetails();
      toast.success("Start date is updated");
    } catch (error) {
      console.log(error);
      toast.error(error.toString().substring(0, 100) + "...");
    }
  };

  const withdrawNative = async () => {
    try {
      await (await presaleContract.withdrawNative()).wait();
      await fetchBalances();
      toast.success("Withdraw successfully");
    } catch (error) {
      console.log(error);
      toast.error(error.toString().substring(0, 100) + "...");
    }
  };

  const withdrawUsdt = async () => {
    try {
      // Convert input to 6 decimals (USDT)
      const usdtWithdrawAmt = ethers.parseUnits(withdrawAmountUsdt, 6);
      await (
        await presaleContract.withdrawTokens(usdtAddresss, usdtWithdrawAmt)
      ).wait();
      await fetchBalances();
      setWithdrawAmountUsdt("0");
      toast.success("Withdraw successfully");
    } catch (error) {
      console.log(error);
      toast.error(error.toString().substring(0, 100) + "...");
    }
  };

  const withdrawTokens = async () => {
    try {
      // Convert withdraw amount (normal input) to 18 decimals
      const withdrawValue = ethers.parseUnits(
        withdrawAmountToken,
        token.decimals
      );
      await (
        await presaleContract.withdrawTokens(tokenAddress, withdrawValue)
      ).wait();
      await fetchBalances();
      setWithDrawAmountToken("0");
      toast.success("Withdraw successfully");
    } catch (error) {
      console.log(error);
      toast.error(error.toString().substring(0, 100) + "...");
    }
  };

  const depositTokens = async () => {
    try {
      // Convert deposit amount (normal input) to 18 decimals
      const depositValue = ethers.parseUnits(
        depositAmountToken,
        token.decimals
      );
      if (!tokenContract) return;
      await (await tokenContract.transfer(presaleAddress, depositValue)).wait();
      await fetchBalances();
      setDepositAmountToken("0");
      toast.success("Deposited Successfully");
    } catch (error) {
      console.log(error);
      toast.error(error.toString().substring(0, 100) + "...");
    }
  };

  const updatePrice = async () => {
    try {
      if (Number(newPrice) === 0) {
        toast.error("Invalid price");
        return;
      }
      // Convert newPrice from normal value (e.g., 3.5) to 8-decimal format
      const priceValue = ethers.parseUnits(newPrice, 8);
      await (await presaleContract.updatePrice(priceValue)).wait();
      await fetchDetails();
      setNewPrice("0");
      toast.success("Price is updated");
    } catch (error) {
      console.log(error);
      toast.error(error.toString());
    }
  };

  const transferOwnership = async () => {
    try {
      if (!presaleContract) return;
      if (!ownerAddress) {
        toast.error("Enter the new owner address");
        return;
      }
      await (
        await presaleContract.transferOwnership(ownerAddress.toString())
      ).wait();
      setOwnerAddress("");
      toast.success("Ownership transferred");
    } catch (error) {
      console.log(error);
      toast.error(error.toString().substring(0, 100) + "...");
    }
  };

  const pauseSale = async () => {
    try {
      if (!presaleContract) return;
      let result = await (await presaleContract.pause()).wait();
      await fetchDetails();
      if (result) toast.success("Sale is Paused");
    } catch (error) {
      console.log(error);
      toast.error(error.toString().substring(0, 100) + "...");
    }
  };

  const resumeSale = async () => {
    try {
      if (!presaleContract) return;
      let result = await (await presaleContract.unpause()).wait();
      await fetchDetails();
      if (result) toast.success("Sale is Resumed");
    } catch (error) {
      console.log(error);
      toast.error(error.toString().substring(0, 100) + "...");
    }
  };

  const fetchSettings = async () => {
    let res = await getSettings();
    console.log(res);
    setSettings(res);
  };
  useEffect(() => {
    if (!presaleContract) return;
    fetchDetails();
    fetchBalances();
  }, [presaleContract]);

  useEffect(() => {
    init();
    fetchSettings();
  }, [walletProvider]);

  // Only display details if connected address equals contract owner
  if (
    !isTestEnv &&
    (!address || address.toLowerCase() !== contractOwner.toLowerCase())
  ) {
    return (
      <Container className="text-center mt-5">
        <h3>{t("info.only_owner")}</h3>
      </Container>
    );
  }

  return (
    <div className="admin-panel-container">
      <div className="admin-panel-header">
        <h2 className="mt-4">{t("dashboard.title")}</h2>
      </div>
      <Container className="mt-4 mb-4">
        <h4>{t("adminPanel.statistics")}</h4>
        <Row xs={1} md={2} lg={3} className="g-4">
          <Col md={6} lg={4}>
            <Card>
              <Card.Body>
                <Card.Title>
                  {t("adminPanel.tokenBalance", { symbol: token.symbol })}
                </Card.Title>
                <h2>
                  {getFirstPart(tokenBalance, token.decimals)} <br />.
                  {getLastPart(tokenBalance, token.decimals)}
                </h2>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6} lg={4}>
            <Card>
              <Card.Body>
                <Card.Title>{t("adminPanel.usdtBalance")}</Card.Title>
                <h2>
                  {getFirstPartUSDT(usdtBalance)} <br />.
                  {getLastPartUSDT(usdtBalance)}
                </h2>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6} lg={4}>
            <Card>
              <Card.Body>
                <Card.Title>
                  {t("adminPanel.bnbBalance", { symbol: network.currency })}
                </Card.Title>
                {/* Display both date and time in UTC if needed elsewhere */}
                <h2>
                  {getFirstPart(nativeBalance)}
                  <br />.{getLastPart(nativeBalance)}
                </h2>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6} lg={4}>
            <Card>
              <Card.Body>
                <Card.Title>
                  {t("adminPanel.tokens_sold", { symbol: token.symbol })}
                </Card.Title>
                <h2>
                  {getFirstPart(soldTokenAmount, token.decimals)}
                  <br />.{getLastPart(soldTokenAmount, token.decimals)}
                </h2>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6} lg={4}>
            <Card style={{ height: "88%" }}>
              <Card.Body>
                <Card.Title>
                  {t("adminPanel.token_price", { symbol: token.symbol })}
                </Card.Title>
                <h2>{new Decimal(price).div(1e8).toFixed(4)}$</h2>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6} lg={4}>
            <Card style={{ height: "88%" }}>
              <Card.Body>
                <Card.Title>{t("adminPanel.startDate")}</Card.Title>
                {/* Display both date and time in UTC */}
                <h5 className="my-3">
                  <i>{startDate.toLocaleString()}</i>
                </h5>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6} lg={4}>
            <Card style={{ height: "88%" }}>
              <Card.Body>
                <Card.Title>{t("adminPanel.endDate")}</Card.Title>
                {/* Display both date and time in UTC */}
                <h5 className="my-3">
                  <i>{endDate.toLocaleString()}</i>
                </h5>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6} lg={4}>
            <Card style={{ height: "88%" }}>
              <Card.Body>
                <Card.Title>
                  {t("adminPanel.sale_status")}: &nbsp; <br />
                  <Badge
                    className="my-3"
                    bg={salePaused ? "danger" : "success"}
                  >
                    {salePaused ? "Paused" : "Active"}
                  </Badge>
                </Card.Title>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6} lg={4}>
            <Card style={{ height: "88%" }}>
              <Card.Body>
                <Card.Title>
                  {t("adminPanel.code_required")}: &nbsp;
                  <br />
                  <Form.Check
                    type="switch"
                    id="sale-status-toggle"
                    label={
                      settings.referralCodeRequired
                        ? t("adminPanel.required")
                        : t("adminPanel.optional")
                    }
                    className="my-3"
                    checked={settings.referralCodeRequired} // true = "Active"
                    onChange={() => {
                      setReferralCodeRequired(
                        !settings.referralCodeRequired
                      ).then(() => {
                        setSettings({
                          ...settings,
                          referralCodeRequired: !settings.referralCodeRequired,
                        });
                        toast.success(t("toasts.settings_updated"));
                      }).catch(()=>{
                        toast.error(t('info.error'))
                      });
                    }}
                  />
                </Card.Title>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6} lg={8}>
            <Card>
              <Card.Body>
                <Card.Title>
                  <b>
                    {t("adminPanel.token_address", { symbol: token.symbol })}
                  </b>
                  : &nbsp;
                  <i>{tokenAddress}</i>
                </Card.Title>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
      <h4 className="mt-4 mb-4">{t("controlPanel.controls")}</h4>
      <Row className="controls-group">
        <Col>
          <Card>
            <Card.Body>
              <h4 className="card-title">
                {t("controlPanel.update_start_time")}
              </h4>
              {/* Use datetime-local input for both date and time */}
              <DateTimePicker
                onChange={setStartDateUpdate}
                value={startDateUpdate}
              />
              <br />
              <Button
                className="mt-3"
                variant="primary"
                onClick={updateStartDate}
              >
                {t("adminPanel.changeStartDate")}
              </Button>
            </Card.Body>
          </Card>
        </Col>
        <Col>
          <Card>
            <Card.Body>
              <h4 className="card-title">
                {t("controlPanel.update_end_time")}
              </h4>
              {/* Use datetime-local input for both date and time */}
              <DateTimePicker
                onChange={setEndDateUpdate}
                value={endDateUpdate}
              />
              <br />
              <Button
                className="mt-3"
                variant="primary"
                onClick={updateEndDate}
              >
                {t("adminPanel.changeEndDate")}
              </Button>
            </Card.Body>
          </Card>
        </Col>
        <Col>
          <Card>
            <Card.Body>
              <h4 className="card-title">
                {t("adminPanel.bnbBalance", { symbol: network.currency })}{" "}
              </h4>
              <h4>
                {getFirstPart(nativeBalance)}
                {"." + getLastPart(nativeBalance)}
              </h4>
              <Button variant="primary" onClick={withdrawNative}>
                {t("adminPanel.withdrawBNB", { symbol: network.currency })}
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Row className="controls-group">
        <Col>
          <Card>
            <Card.Body>
              <h4 className="card-title">
                {t("controlPanel.update_token_price", { symbol: token.symbol })}
              </h4>
              <h4>
                {t("controlPanel.current_price")}:{" "}
                {new Decimal(price).div(1e8).toFixed(4)}$
              </h4>
              <Form.Control
                type="number"
                value={newPrice}
                onChange={(event) => setNewPrice(event.target.value.toString())}
                placeholder="Enter new price (e.g., 3.5)"
              />
              <Button className="mt-3" variant="primary" onClick={updatePrice}>
                {t("controlPanel.update_price")}
              </Button>
            </Card.Body>
          </Card>
        </Col>
        <Col>
          <Card>
            <Card.Body>
              <h4 className="card-title">{t("adminPanel.usdtBalance")}</h4>
              <h4>
                {getFirstPartUSDT(usdtBalance)}
                {"." + getLastPartUSDT(usdtBalance)}
              </h4>
              <Form.Control
                type="number"
                value={withdrawAmountUsdt}
                onChange={(event) =>
                  setWithdrawAmountUsdt(event.target.value.toString())
                }
                placeholder="Withdraw USDT (e.g., 1.23)"
              />
              <Button className="mt-3" variant="primary" onClick={withdrawUsdt}>
                {t("adminPanel.withdrawUSDT")}
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col>
          <Card>
            <Card.Body>
              <h4 className="card-title">
                {t("adminPanel.tokenBalance", { symbol: token.symbol })}
              </h4>
              <h4>
                {getFirstPart(tokenBalance, token.decimals)}.
                {getLastPart(tokenBalance, token.decimals)}
              </h4>
              <Row>
                <Col className="pt-3 pb-3">
                  <Form.Control
                    type="number"
                    placeholder="Deposit amount (e.g., 5.0)"
                    value={depositAmountToken}
                    onChange={(event) =>
                      setDepositAmountToken(event.target.value.toString())
                    }
                  />
                  <br />
                  <Button
                    className="w-100"
                    variant="primary"
                    onClick={depositTokens}
                  >
                    {t("adminPanel.deposit")}
                  </Button>
                </Col>
                <Col className="pt-3 pb-3">
                  <Form.Control
                    type="number"
                    placeholder="Withdraw amount (e.g., 2.5)"
                    value={withdrawAmountToken}
                    onChange={(event) =>
                      setWithDrawAmountToken(event.target.value.toString())
                    }
                  />
                  <br />
                  <Button
                    className="w-100"
                    variant="primary"
                    onClick={withdrawTokens}
                  >
                    {t("adminPanel.withdraw")}
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col xs={12} md={8}>
          <Card className="mb-3">
            <Card.Body>
              <h4>
                <b>{t("adminPanel.changeOwnerShip")}</b>
              </h4>
              <Form>
                <Form.Group
                  as={Row}
                  className="mb-3"
                  controlId="formPlaintextEmail"
                >
                  <Form.Label column sm="2">
                    {t("adminPanel.address")}:
                  </Form.Label>
                  <Col sm="10">
                    <Form.Control
                      type="text"
                      value={ownerAddress}
                      onChange={(event) =>
                        setOwnerAddress(event.target.value.toString())
                      }
                    />
                  </Col>
                </Form.Group>
                <Button
                  variant="primary"
                  onClick={transferOwnership}
                  style={{ marginLeft: "40%" }}
                >
                  {t("adminPanel.transfer")}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} md={4}>
          <Card className="mb-3">
            <Card.Body>
              <br />
              <h4 style={{ textAlign: "center" }}>
                {t("adminPanel.sale_paused")} : {salePaused.toString()}
              </h4>
              <br />
              <div style={{ display: "flex", justifyContent: "space-evenly" }}>
                <Button
                  className="me-3"
                  variant="danger"
                  disabled={salePaused}
                  onClick={pauseSale}
                >
                  {t("adminPanel.stopSale")}
                </Button>
                <Button
                  variant="success"
                  disabled={!salePaused}
                  onClick={resumeSale}
                >
                  {t("adminPanel.unfreeze")}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AdminPanel;
